import { useEffect, useRef, useState, useCallback } from 'react';
import { parseQuestionsRealtime, parseQuestionsRealtimeStream, ParsedQuestionFromAPI, SurveyMode } from '../utils/simulationApi';
import { ParsedQuestion } from '../components/survey/types';
import { TYPE_CODE_TO_FULL } from './typeCodeMap';

const DEBOUNCE_MS = 1200;

export interface UseRealtimeParsingResult {
  parsedQuestions: ParsedQuestion[];
  /** True during the very first parse when no cached results exist yet. */
  isParsing: boolean;
  /** True during a re-parse while stale (but valid) results are still visible. */
  isUpdating: boolean;
  /**
   * Latest error message from the realtime parse call (e.g. "raw_text
   * exceeds 15000 character limit for realtime parsing"). `null` when the
   * most recent attempt either succeeded, was aborted, was a no-op, or has
   * not run yet. Consumers should surface this to the user — silent
   * swallowing strands the respondent.
   */
  error: string | null;
  /** Feed user edits (type changes, images) back into the cache so they survive re-parses. */
  seedCache: (questions: ParsedQuestion[]) => void;
  /** Hard-reset the hook: clear cache + results (used by "Back to Questions"). */
  reset: () => void;
  /** Number of questions detected (for skeleton loading UI) */
  questionCount: number;
  /** Which question indices are still loading (for skeleton UI) */
  loadingIndices: Set<number>;
}

function normalizeText(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

function mapApiQuestion(q: ParsedQuestionFromAPI, fallbackId: string): ParsedQuestion {
  return {
    id: q.id || fallbackId,
    text: q.text,
    type: q.type,
    choices: q.choices,
    scale_min: q.scale_min,
    scale_max: q.scale_max,
    max_selections: q.max_selections,
    ranking_count: q.ranking_count,
    text_limit: q.text_limit,
    image_paths: q.image_paths,
    image_descriptions: q.image_descriptions,
    image_attachment_level: q.image_attachment_level as 'question' | 'option' | undefined,
    option_images: q.option_images,
    maxdiff_items: q.maxdiff_items,
    conjoint_attributes: q.conjoint_attributes,
    conjoint_description: q.conjoint_description,
    matrix_rows: q.matrix_rows,
    matrix_columns: q.matrix_columns,
  };
}

/** Merge user edits from a cached question onto a fresh API result.
 *
 * - Fields from **parsing** (choices, matrix data) always use fresh —
 *   they are re-extracted from the latest textarea text each time.
 *   `null` from the API means "no choices" (user removed them), NOT
 *   "unknown"; we must NOT fall back to stale cached choices.
 * - Fields from **user intent** (type, images, scale config) prefer
 *   cached — the user explicitly set them in the preview.
 */
function mergeWithCached(fresh: ParsedQuestion, cached: ParsedQuestion): ParsedQuestion {
  return {
    ...fresh,
    // User-intent fields: prefer cached
    type: cached.type,
    scale_min: cached.scale_min ?? fresh.scale_min,
    scale_max: cached.scale_max ?? fresh.scale_max,
    max_selections: cached.max_selections ?? fresh.max_selections,
    ranking_count: cached.ranking_count ?? fresh.ranking_count,
    text_limit: cached.text_limit ?? fresh.text_limit,
    image_paths: cached.image_paths?.length ? cached.image_paths : fresh.image_paths,
    image_descriptions: cached.image_descriptions?.length ? cached.image_descriptions : fresh.image_descriptions,
    image_attachment_level: cached.image_attachment_level ?? fresh.image_attachment_level,
    option_images: cached.option_images && Object.keys(cached.option_images).length > 0
      ? cached.option_images : fresh.option_images,
    // Parsed-from-text fields: always use fresh (no fallback to stale cache)
    choices: fresh.choices,
    maxdiff_items: fresh.maxdiff_items,
    conjoint_attributes: fresh.conjoint_attributes,
    conjoint_description: fresh.conjoint_description,
    matrix_rows: fresh.matrix_rows,
    matrix_columns: fresh.matrix_columns,
  };
}

/**
 * Dual-key cache: stores ParsedQuestion objects keyed by both question ID
 * and normalized text. ID is the primary lookup; text is a fallback when
 * the AI re-assigns IDs.
 */
class QuestionCache {
  private byId = new Map<string, ParsedQuestion>();
  private byText = new Map<string, ParsedQuestion>();

  set(q: ParsedQuestion) {
    this.byId.set(q.id, q);
    this.byText.set(normalizeText(q.text), q);
  }

  /** Look up by ID first, then fall back to normalized text. */
  get(id: string, text: string): ParsedQuestion | undefined {
    return this.byId.get(id) ?? this.byText.get(normalizeText(text));
  }

  /** Remove entries whose IDs are NOT in the given set (stale trim). */
  retainIds(ids: Set<string>) {
    for (const [key, q] of this.byId) {
      if (!ids.has(key)) {
        this.byId.delete(key);
        this.byText.delete(normalizeText(q.text));
      }
    }
  }

  clear() {
    this.byId.clear();
    this.byText.clear();
  }

  get size() { return this.byId.size; }
}

/**
 * Realtime question parsing hook with dual-key caching.
 *
 * - Debounces textarea input, sends text to backend for Haiku tagging
 * - Dual-key cache (ID + text) preserves user edits for unchanged questions
 * - Never clears visible results mid-parse; shows stale data with `isUpdating` flag
 * - Append-only optimisation: only sends the new tail when user appends text
 */
export default function useRealtimeQuestionTags(
  questionsText: string,
  goal: string,
  context: string,
  enabled: boolean = true,
  useTwoPassParsing: boolean = true,
  surveyMode: SurveyMode = 'ai_sim',
): UseRealtimeParsingResult {
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [loadingIndices, setLoadingIndices] = useState<Set<number>>(new Set());

  const abortRef = useRef<AbortController | null>(null);
  const streamAbortRef = useRef<{ abort: () => void } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lastParsedTextRef = useRef<string>('');
  const lastResultRef = useRef<ParsedQuestion[]>([]);
  const cacheRef = useRef(new QuestionCache());

  const cancel = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    if (streamAbortRef.current) { streamAbortRef.current.abort(); streamAbortRef.current = null; }
  }, []);

  const seedCache = useCallback((questions: ParsedQuestion[]) => {
    const cache = cacheRef.current;
    for (const q of questions) {
      cache.set(q);
    }
    lastResultRef.current = questions;
  }, []);

  const reset = useCallback(() => {
    cancel();
    setParsedQuestions([]);
    setIsParsing(false);
    setIsUpdating(false);
    setError(null);
    setQuestionCount(0);
    setLoadingIndices(new Set());
    lastParsedTextRef.current = '';
    lastResultRef.current = [];
    cacheRef.current.clear();
  }, [cancel]);

  useEffect(() => {
    console.log('[useRealtimeQuestionTags] useEffect triggered, enabled:', enabled, 'textLength:', questionsText.length, 'useTwoPassParsing:', useTwoPassParsing);
    if (!enabled) {
      console.log('[useRealtimeQuestionTags] Disabled, returning early');
      cancel();
      setIsUpdating(false);
      return;
    }

    const trimmed = questionsText.trim();

    if (!trimmed) {
      console.log('[useRealtimeQuestionTags] Empty text, clearing');
      cancel();
      setParsedQuestions([]);
      setIsParsing(false);
      setIsUpdating(false);
      setError(null);
      lastParsedTextRef.current = '';
      lastResultRef.current = [];
      cacheRef.current.clear();
      return;
    }

    if (trimmed === lastParsedTextRef.current) {
      console.log('[useRealtimeQuestionTags] Same text as before, skipping');
      return;
    }

    console.log('[useRealtimeQuestionTags] New text detected, starting debounce timer');
    cancel();

    const hasExisting = lastResultRef.current.length > 0;
    if (hasExisting) {
      setIsUpdating(true);
    }

    timerRef.current = setTimeout(async () => {
      console.log('[useRealtimeQuestionTags] Debounce timer fired, hasExisting:', hasExisting);
      if (!hasExisting) {
        setIsParsing(true);
      }

      const lastText = lastParsedTextRef.current;
      const cache = cacheRef.current;

      // Clear cache on full replacement
      if (lastText.length > 0) {
        const minLen = Math.min(lastText.length, trimmed.length);
        let prefixLen = 0;
        while (prefixLen < minLen && lastText[prefixLen] === trimmed[prefixLen]) {
          prefixLen++;
        }
        let suffixLen = 0;
        const maxSuffix = minLen - prefixLen;
        while (
          suffixLen < maxSuffix &&
          lastText[lastText.length - 1 - suffixLen] === trimmed[trimmed.length - 1 - suffixLen]
        ) {
          suffixLen++;
        }
        const sharedLen = prefixLen + suffixLen;
        if (sharedLen < lastText.length * 0.3) {
          cache.clear();
        }
      }

      // Use streaming for two-pass parsing
      console.log('[useRealtimeQuestionTags] useTwoPassParsing:', useTwoPassParsing);
      if (useTwoPassParsing) {
        console.log('[useRealtimeQuestionTags] Starting streaming parse...');
        // Streaming mode: show skeletons immediately after Pass 1
        const questionsMap = new Map<number, ParsedQuestion>();

        const stream = parseQuestionsRealtimeStream(trimmed, goal, context, {
          onBoundaries: (boundaries) => {
            console.log('[useRealtimeQuestionTags] onBoundaries received:', boundaries.length);
            // Create skeleton questions from boundaries
            const count = boundaries.length;
            setQuestionCount(count);
            setLoadingIndices(new Set(boundaries.map((_, i) => i)));

            // Create initial skeleton questions with type from boundary
            const skeletons: ParsedQuestion[] = boundaries.map((b, i) => ({
              id: `Q${i + 1}`,
              text: `Loading question ${i + 1}...`,
              type: TYPE_CODE_TO_FULL[b.t] || 'long_text',
              isLoading: true,
            } as ParsedQuestion));

            setParsedQuestions(skeletons);
            setIsParsing(false); // Pass 1 done, show skeletons
            setIsUpdating(false); // Also clear updating so preview renders
          },
          onQuestion: (index, question) => {
            const mapped = mapApiQuestion(question, `Q${index + 1}`);
            const cached = cache.get(mapped.id, mapped.text);
            const final = cached ? mergeWithCached(mapped, cached) : mapped;

            questionsMap.set(index, final);
            cache.set(final);

            // Update loading indices
            setLoadingIndices(prev => {
              const next = new Set(prev);
              next.delete(index);
              return next;
            });

            // Update questions array
            setParsedQuestions(prev => {
              const updated = [...prev];
              updated[index] = final;
              return updated;
            });
          },
          onComplete: () => {
            setIsUpdating(false);
            setLoadingIndices(new Set());
            lastParsedTextRef.current = trimmed;
            lastResultRef.current = Array.from(questionsMap.entries())
              .sort(([a], [b]) => a - b)
              .map(([, q]) => q);
          },
          onError: (message) => {
            setError(message);
            setIsParsing(false);
            setIsUpdating(false);
          },
        }, surveyMode);

        streamAbortRef.current = stream;
      } else {
        // Non-streaming mode: use regular API
        const controller = new AbortController();
        abortRef.current = controller;

        try {
          const isAppendOnly = lastText.length > 0 && trimmed.startsWith(lastText);

          let textToSend: string;
          if (isAppendOnly) {
            textToSend = trimmed.substring(lastText.length);
            if (!textToSend.trim()) {
              setIsParsing(false);
              setIsUpdating(false);
              return;
            }
          } else {
            textToSend = trimmed;
          }

          const result = await parseQuestionsRealtime(
            textToSend, goal, context, controller.signal, surveyMode, false,
          );
          if (controller.signal.aborted) return;

          setError(null);

          const apiQuestions = result.questions.map(
            (q: ParsedQuestionFromAPI, i: number) => mapApiQuestion(q, `Q${i + 1}`),
          );

          let finalQuestions: ParsedQuestion[];

          if (isAppendOnly) {
            const startNum = lastResultRef.current.length + 1;
            const renumbered = apiQuestions.map((q, i) => ({
              ...q,
              id: `Q${startNum + i}`,
            }));
            finalQuestions = [...lastResultRef.current, ...renumbered];
          } else {
            finalQuestions = apiQuestions.map((q, i) => {
              const id = q.id || `Q${i + 1}`;
              const cached = cache.get(id, q.text);
              if (cached) {
                return mergeWithCached({ ...q, id }, cached);
              }
              return { ...q, id };
            });
          }

          const activeIds = new Set(finalQuestions.map(q => q.id));
          for (const q of finalQuestions) {
            cache.set(q);
          }
          cache.retainIds(activeIds);

          lastParsedTextRef.current = trimmed;
          lastResultRef.current = finalQuestions;
          setParsedQuestions(finalQuestions);
        } catch (err: unknown) {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          if (err instanceof Error && err.name === 'AbortError') return;
          const message =
            err instanceof Error && err.message
              ? err.message
              : 'Realtime question parsing failed.';
          console.warn('Realtime question parsing failed:', err);
          setError(message);
        } finally {
          if (!controller.signal.aborted) {
            setIsParsing(false);
            setIsUpdating(false);
          }
        }
      }
    }, DEBOUNCE_MS);

    return () => cancel();
  }, [questionsText, goal, context, enabled, useTwoPassParsing, surveyMode, cancel]);

  useEffect(() => {
    return () => cancel();
  }, [cancel]);

  return { parsedQuestions, isParsing, isUpdating, error, seedCache, reset, questionCount, loadingIndices };
}
